import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface TeamConfig {
  id: string;
  name: string;
  priority: number;
  captainId: string | null;
  captainName: string | null;
  viceCaptainId: string | null;
  viceCaptainName: string | null;
}

export function useTeamConfig(clubId: string | undefined) {
  const [teams, setTeams] = useState<TeamConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId) {
      setTeams([]);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      collection(db, "clubs", clubId, "teamConfig"),
      (snap) => {
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() } as TeamConfig))
          .sort((a, b) => (a.priority || 99) - (b.priority || 99));
        setTeams(items);
        setLoading(false);
      }
    );

    return unsub;
  }, [clubId]);

  const getTeamConfig = (teamName: string): TeamConfig | undefined => {
    return teams.find((t) => t.name === teamName);
  };

  const isCaptainOf = (teamName: string, userId: string): boolean => {
    const config = getTeamConfig(teamName);
    if (!config) return false;
    return config.captainId === userId || config.viceCaptainId === userId;
  };

  const isAnyCaptain = (userId: string | undefined): boolean => {
    if (!userId) return false;
    return teams.some((t) => t.captainId === userId || t.viceCaptainId === userId);
  };

  const getNextTeamInCascade = (teamName: string): TeamConfig | undefined => {
    const config = getTeamConfig(teamName);
    if (!config) return undefined;
    return teams.find((t) => t.priority === config.priority + 1);
  };

  return { teams, getTeamConfig, isCaptainOf, isAnyCaptain, getNextTeamInCascade, loading };
}
